import { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MODULE_CATALOG, moduleAccessibleFor } from "../lib/modules";
import api from "../lib/api";
import { Sparkles, X, ChevronLeft, ChevronRight, SkipForward, CheckCircle2 } from "lucide-react";

/**
 * OnboardingTour — first-login guided walkthrough.
 * Persistence: a per-user localStorage key (`pk-tour-done-<userId>`) is set
 * synchronously the moment the user finishes or skips so the tour never
 * re-runs on this device, even if the network call fails. The server flag
 * `tutorial_completed` provides cross-device sync.
 */
const TOUR_VERSION = "v1";
const tourDoneKey = (uid) => `pk-tour-done-${uid || "anon"}`;

function getRectFor(testId) {
  if (!testId) return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  // Use viewport-relative rect; we render via portal at document level.
  return el.getBoundingClientRect();
}

function scrollIntoViewIfNeeded(testId) {
  if (!testId) return;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return;
  try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }); } catch { /* */ }
}

/** Build the step list dynamically based on the user's modules. */
function buildSteps(user) {
  if (!user) return [];
  const steps = [
    {
      id: "welcome",
      target: null,
      title: `Welcome aboard, ${user.name?.split(" ")[0] || "team"} 👋`,
      body: "Quick 60-second tour of your Panchakosh Ventures workspace. You can skip anytime — replay it later from your profile menu.",
      placement: "center",
    },
    {
      id: "sidebar",
      target: "desktop-sidebar",
      title: "Your modules live here",
      body: "Everything you have access to is grouped in the left sidebar — Workspace, HR, Sales, CRM, Documentation, etc. Click the chevron at the top to collapse it into icon-only mode.",
      placement: "right",
    },
    {
      id: "dashboard",
      target: "sidebar-dashboard-link",
      title: "Your Dashboard",
      body: "Punch In/Out, attendance, leave balance, pending tasks, call requests, documents and announcements — all in one calm snapshot.",
      placement: "right",
      navigate: "/",
    },
    {
      id: "notif",
      target: "notif-bell",
      title: "Notification Centre",
      body: "All cross-module updates land here — Orders, Invoices, Dispatch, CRM, Leaves, Travel. Filter by Unread or By Module. We chime softly when something new arrives.",
      placement: "bottom",
    },
    {
      id: "theme",
      target: "theme-toggle",
      title: "Light or Dark — your choice",
      body: "Toggle between Light and Dark themes anytime. Your preference is remembered across devices.",
      placement: "bottom",
    },
    {
      id: "user-menu",
      target: "user-menu-trigger",
      title: "Your profile",
      body: "Upload your photo, change your password, replay this tour, or sign out — all from here. Your photo shows up in the Employee Directory too.",
      placement: "right",
    },
  ];

  // Add a step per accessible module group (max 1 per major module). Capped to
  // keep the tour digestible (~10 module spotlights max).
  const moduleSteps = [];
  const seenGroups = new Set();
  Object.entries(MODULE_CATALOG).forEach(([key, mod]) => {
    if (mod.hiddenInSidebar) return;
    if (!moduleAccessibleFor(user, key)) return;
    if (seenGroups.has(mod.group)) return; // one step per group is enough
    seenGroups.add(mod.group);
    moduleSteps.push({
      id: `module-${key}`,
      target: `sidebar-${key}-link`,
      title: `${mod.label}`,
      body: `Part of the ${mod.group} group. Click to explore — you can come back to this tour anytime from your profile menu.`,
      placement: "right",
    });
  });
  // Add workspace fixed links too (Directory / Tasks / My Sales)
  const fixedWorkspaceLinks = [
    { test: "sidebar-directory-link", label: "Employee Directory", desc: "Find anyone — punch status, birthdays, work anniversaries and farewell wishes." },
    { test: "sidebar-tasks-link",      label: "Tasks",              desc: "Your assigned tasks across the org. Pending Review and Overdue tiles show on the Dashboard too." },
    { test: "sidebar-my-attendance-link", label: "My Attendance",   desc: "View your monthly attendance, regularisations and leave history at a glance." },
  ];
  fixedWorkspaceLinks.forEach((l) => {
    if (document.querySelector(`[data-testid="${l.test}"]`)) {
      moduleSteps.push({ id: l.test, target: l.test, title: l.label, body: l.desc, placement: "right" });
    }
  });

  // Cap module steps to 10 to avoid fatigue.
  steps.push(...moduleSteps.slice(0, 10));

  steps.push({
    id: "done",
    target: null,
    title: "You're all set ✨",
    body: "That's the tour. Anything you need is one click away in the sidebar. Replay this tour anytime from your profile menu.",
    placement: "center",
    isFinal: true,
  });
  return steps;
}

/** Compute floating-card position around the target rect. */
function computeCardPos(rect, placement) {
  const PAD = 16;
  const CARD_W = 360;
  const CARD_H = 200;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect || placement === "center") {
    return {
      top: Math.max(24, vh / 2 - CARD_H / 2),
      left: Math.max(24, vw / 2 - CARD_W / 2),
    };
  }
  let top, left;
  switch (placement) {
    case "right":
      top = rect.top + rect.height / 2 - CARD_H / 2;
      left = rect.right + PAD;
      break;
    case "left":
      top = rect.top + rect.height / 2 - CARD_H / 2;
      left = rect.left - CARD_W - PAD;
      break;
    case "top":
      top = rect.top - CARD_H - PAD;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
    case "bottom":
    default:
      top = rect.bottom + PAD;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
  }
  // Clamp into viewport
  top = Math.min(Math.max(top, 24), vh - CARD_H - 24);
  left = Math.min(Math.max(left, 24), vw - CARD_W - 24);
  return { top, left };
}

export default function OnboardingTour() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [, force] = useState(0);
  // Once this user has auto-started (or dismissed) the tour during this
  // mount, never auto-start again — React StrictMode + re-renders would
  // otherwise re-fire the auto-open effect after close().
  const hasAutoStartedRef = useRef(false);

  const steps = useMemo(() => buildSteps(user), [user]);
  const step = steps[stepIdx];

  // Decide whether to auto-open the tour.
  useEffect(() => {
    if (!user) return;
    if (hasAutoStartedRef.current) return;
    // Don't show during forced password change.
    if (user.password_needs_upgrade || user.is_default_password) return;
    // Don't show to temp/candidate roles.
    if (user.role === "candidate_temp" || user.role === "ex_employee_temp") return;
    // MANDATORY mode wins above everything — always run the tour, ignore the
    // local "done" key and the server `tutorial_completed` flag.
    if (user.tutorial_mandatory) {
      try { localStorage.removeItem(tourDoneKey(user.id)); } catch { /* */ }
      hasAutoStartedRef.current = true;
      const t = setTimeout(() => { setStepIdx(0); setActive(true); }, 700);
      return () => clearTimeout(t);
    }
    // Replay flag wins — but only consume it once.
    const replay = (() => { try { return localStorage.getItem("pk-replay-tour") === "1"; } catch { return false; } })();
    if (replay) {
      try { localStorage.removeItem("pk-replay-tour"); } catch { /* */ }
      try { localStorage.removeItem(tourDoneKey(user.id)); } catch { /* */ }
      hasAutoStartedRef.current = true;
      setStepIdx(0);
      setActive(true);
      return;
    }
    // Already completed (server flag OR local "done" key)?
    const localDone = (() => { try { return localStorage.getItem(tourDoneKey(user.id)) === "1"; } catch { return false; } })();
    if (user.tutorial_completed || localDone) {
      hasAutoStartedRef.current = true;
      return;
    }
    // First-login bootstrap — defer briefly so the layout DOM is ready.
    hasAutoStartedRef.current = true;
    const t = setTimeout(() => { setStepIdx(0); setActive(true); }, 700);
    return () => clearTimeout(t);
  }, [user]);

  // Recompute target rect whenever the active step changes, and on resize/scroll.
  const recompute = useCallback(() => {
    if (!active || !step) return;
    if (step.navigate) {
      // Already navigated via useEffect below.
    }
    const r = getRectFor(step.target);
    setRect(r);
    force((x) => x + 1);
  }, [active, step]);

  useLayoutEffect(() => {
    if (!active || !step) return;
    if (step.navigate) {
      navigate(step.navigate);
    }
    scrollIntoViewIfNeeded(step.target);
    // Give the DOM a tick to update after route change or scroll.
    const t = setTimeout(recompute, 220);
    return () => clearTimeout(t);
  }, [active, step, recompute, navigate]);

  useEffect(() => {
    if (!active) return;
    const onChange = () => recompute();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [active, recompute]);

  const close = useCallback(async (markDone = true) => {
    setActive(false);
    if (markDone) {
      // Synchronous local guard FIRST so even a failing API call won't
      // resurrect the tour on the next route change.
      try { if (user?.id) localStorage.setItem(tourDoneKey(user.id), "1"); } catch { /* */ }
      try { await api.post("/auth/tutorial-completed"); } catch { /* silent */ }
      // Refresh user state so other parts of the app see tutorial_completed=true.
      try { if (refresh) await refresh(); } catch { /* */ }
    }
  }, [refresh, user]);

  if (!active || !step) return null;

  const cardPos = computeCardPos(rect, step.placement);
  const isCenter = step.placement === "center" || !rect;
  const total = steps.length;
  // Mandatory mode: when the server flag `tutorial_mandatory` is true,
  // the user CANNOT skip or close — only Finish on the last step
  // dismisses the tour. Typically set by Master Admin after a deploy.
  const mandatory = !!user?.tutorial_mandatory;

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483646] pointer-events-none"
      data-testid="onboarding-tour-overlay"
      aria-live="polite"
    >
      {/* Darkening backdrop. When we have a target rect, we draw a transparent
          "cutout" using box-shadow so the rest of the page is dimmed. */}
      {isCenter ? (
        <div
          className={`absolute inset-0 bg-slate-950/70 backdrop-blur-[2px] ${mandatory ? "" : "pointer-events-auto"}`}
          onClick={mandatory ? undefined : () => close(true)}
        />
      ) : (
        <div
          className="absolute pointer-events-auto"
          style={{
            top: Math.max(0, rect.top - 8),
            left: Math.max(0, rect.left - 8),
            width: rect.width + 16,
            height: rect.height + 16,
            borderRadius: 12,
            boxShadow:
              "0 0 0 9999px rgba(2,6,23,0.72), 0 0 0 3px rgba(99,102,241,0.75), 0 0 24px 4px rgba(99,102,241,0.55)",
            transition: "all 220ms ease-out",
          }}
        />
      )}

      {/* Floating card */}
      <div
        className="absolute pointer-events-auto rounded-2xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-2xl shadow-indigo-900/40 border border-indigo-200/70 dark:border-indigo-500/30 w-[360px] p-5"
        style={{
          top: cardPos.top,
          left: cardPos.left,
          transition: "top 220ms ease-out, left 220ms ease-out",
        }}
        data-testid="onboarding-tour-card"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-bold text-indigo-700 dark:text-indigo-300">
            <span className="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-500/20 grid place-items-center">
              <Sparkles size={12} />
            </span>
            Portal Tour · {stepIdx + 1} / {total}
            {mandatory && (
              <span className="ml-1 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full border border-amber-200">
                Required
              </span>
            )}
          </div>
          {!mandatory && (
            <button
              onClick={() => close(true)}
              className="w-7 h-7 grid place-items-center rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
              aria-label="Close tour"
              data-testid="onboarding-tour-close"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <h3 className="heading text-[17px] font-bold leading-snug">{step.title}</h3>
        <p className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">{step.body}</p>
        {mandatory && stepIdx === 0 && (
          <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md p-2 mt-2">
            This walkthrough was set as required by your admin. Please complete all {total} steps to continue.
          </p>
        )}

        {/* Progress bar */}
        <div className="mt-4 h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-indigo-600 transition-all"
            style={{ width: `${((stepIdx + 1) / total) * 100}%` }}
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          {mandatory ? (
            <span className="text-[10.5px] font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <Sparkles size={11} /> Skip disabled
            </span>
          ) : (
            <button
              onClick={() => close(true)}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 flex items-center gap-1"
              data-testid="onboarding-tour-skip"
            >
              <SkipForward size={12} /> Skip tour
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <button
              disabled={stepIdx === 0}
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              className="h-8 px-3 rounded-md text-[12px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="onboarding-tour-back"
            >
              <ChevronLeft size={12} /> Back
            </button>
            {step.isFinal ? (
              <button
                onClick={() => close(true)}
                className="h-8 px-3 rounded-md text-[12px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                data-testid="onboarding-tour-finish"
              >
                <CheckCircle2 size={13} /> Finish
              </button>
            ) : (
              <button
                onClick={() => setStepIdx((i) => Math.min(total - 1, i + 1))}
                className="h-8 px-3 rounded-md text-[12px] font-bold bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1"
                data-testid="onboarding-tour-next"
              >
                Next <ChevronRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Helper used by Layout to trigger the replay link from the profile menu. */
export function replayOnboardingTour(navigate) {
  try { localStorage.setItem("pk-replay-tour", "1"); } catch { /* */ }
  try { window.location.reload(); } catch { if (navigate) navigate(0); }
}

// Mark tour version (kept exported so future migrations can branch).
export { TOUR_VERSION };
